/**
 * DashboardHome — seller portal home page.
 *
 * Shows shop status header, setup nudges (only when a completeness flag is
 * false), catalog snapshot (4 StatTiles), quick-action links, and a
 * buyer-interest strip. Redirects to /account on 403 not_a_seller.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { Card, StatTile, Skeleton, GPill } from "@garden/ui";

import { registerSellerPortalI18n, SELLER_PORTAL_NS } from "@/features/seller-portal/i18n";
import { useSellerOverview } from "@/features/seller-portal/hooks";
import { ApiError } from "@/lib/api-client";

export function DashboardHome(): ReactElement {
  registerSellerPortalI18n();
  const { t } = useTranslation(SELLER_PORTAL_NS);
  const { data, isLoading, error } = useSellerOverview();

  if (error instanceof ApiError && error.code === "forbidden.not_a_seller") {
    return <Navigate to="/account" replace />;
  }

  if (isLoading || !data) {
    return (
      <div data-testid="home-loading">
        <Skeleton w="100%" h={160} />
      </div>
    );
  }

  const c = data.catalog;
  const nudges = [
    !data.completeness.location_set ? t("home.nudges.location") : null,
    !data.completeness.hours_set ? t("home.nudges.hours") : null,
    !data.completeness.contacts_set ? t("home.nudges.contacts") : null,
  ].filter((x): x is string => x !== null);

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
      {/* Status header */}
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-foreground">
          {data.shop.business_name}
        </h1>
        {data.shop.market_label ? (
          <span className="text-sm text-[var(--g-ink-3)]">
            {data.shop.market_label}
          </span>
        ) : null}
        <GPill
          tone={data.shop.verification_status === "verified" ? "primary" : "neutral"}
          size="sm"
        >
          {t(`home.status.${data.shop.verification_status}`)}
        </GPill>
        <GPill tone={data.shop.vacation_mode ? "warn" : "neutral"} size="sm">
          {t(data.shop.vacation_mode ? "home.status.vacationOn" : "home.status.vacationOff")}
        </GPill>
      </header>

      {/* Setup nudges — only when at least one flag is false */}
      {nudges.length > 0 ? (
        <div data-testid="home-nudges">
          <Card padding={16}>
            <p className="mb-2 text-sm font-semibold text-foreground">
              {t("home.nudges.title")}
            </p>
            <ul className="flex flex-col gap-1 text-sm text-[var(--g-ink-2)]">
              {nudges.map((n) => (
                <li key={n}>• {n}</li>
              ))}
            </ul>
          </Card>
        </div>
      ) : null}

      {/* Catalog snapshot */}
      <section>
        <p className="mb-2 text-sm font-semibold text-foreground">
          {t("home.catalog.title")}
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div data-testid="home-catalog-active">
            <StatTile label={t("home.catalog.active")} value={String(c.active)} />
          </div>
          <div data-testid="home-catalog-draft">
            <StatTile label={t("home.catalog.draft")} value={String(c.draft)} />
          </div>
          <div data-testid="home-catalog-pending">
            <StatTile label={t("home.catalog.pending")} value={String(c.pending_approval)} />
          </div>
          <div data-testid="home-catalog-oos">
            <StatTile label={t("home.catalog.outOfStock")} value={String(c.out_of_stock)} />
          </div>
        </div>
      </section>

      {/* Quick actions */}
      <section>
        <p className="mb-2 text-sm font-semibold text-foreground">
          {t("home.actions.title")}
        </p>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/sellers/me/products/new"
            className="rounded-[var(--g-radius)] bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            {t("home.actions.addProduct")}
          </Link>
          <Link
            to="/sellers/me/settings"
            className="rounded-[var(--g-radius)] border border-border px-4 py-2 text-sm font-semibold text-foreground"
          >
            {t("home.actions.editShop")}
          </Link>
        </div>
      </section>

      {/* Buyer-interest strip */}
      <div data-testid="home-buyer-interest">
        <Card padding={16}>
          <p className="text-sm font-semibold text-foreground">
            {t("home.interest.title")}
          </p>
          <p className="text-sm text-[var(--g-ink-2)]">
            {t("home.interest.taps", { count: data.buyer_interest.contact_taps_7d })}
          </p>
        </Card>
      </div>
    </div>
  );
}
